import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, computed, input, OnInit, output, signal } from '@angular/core';
import { DocumentConfigPartial, DocumentItemPartial, DocumentItemTypeEnum } from '@domain';
import { TranslatePipe } from '@ngx-translate/core';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { RaViewerTextComponent } from 'src/app/shared/components/ra-viewer-text/ra-viewer-text.component';

import { resolveSingleImageSize } from '../../../../pdf-renderer/image-size';
import { itemTextColor, itemTextSizeRem } from '../../../../text-style-options';
@Component({
    selector: 'image-data',
    templateUrl: 'image-data.component.html',
    styleUrls: ['image-data.component.scss', '../../document-preview.scss'],
    imports: [DatePipe, DecimalPipe, TranslatePipe, RaViewerTextComponent, RaIconComponent],
})
export class ImageDataComponent implements OnInit {
    public config = input.required<DocumentConfigPartial>();
    public item = input.required<DocumentItemPartial>();
    /** Tamanho e cor próprios da descrição (configs textSize/textColor), como no PDF. */
    public descriptionSize = computed(() => itemTextSizeRem(this.item()?.configs));
    public descriptionColor = computed(() => itemTextColor(this.item()?.configs));
    /** Logo da company, usado como marca d'água quando habilitada na config. */
    public companyLogo = input<string | undefined>(undefined);

    /**
     * Índice (dentro DESTE item) da foto clicada. A tela cheia não é mais deste
     * componente: ela é única e vive no preview, mostrando a galeria do documento
     * inteiro — quem sabe converter "foto N deste item" em "foto N do documento"
     * é a página, que conhece a ordem das seções.
     */
    public imageOpen = output<number>();

    /**
     * Marca d'água só aparece quando ligada na config E a company tem logo.
     * Sem logo não há o que sobrepor — mesma regra do PDF.
     */
    public showWatermark = computed<boolean>(
        () => (this.config()?.showImageWatermark ?? false) && !!this.companyLogo()
    );

    /**
     * Visibilidade de número/data por imagem: a config por-item (item.configs,
     * definida na tela de configurações da imagem/grupo) tem precedência sobre a
     * config global do documento. Sem isso, desligar "Numerar imagens"/"Data" no
     * item não era respeitado no preview (lia só a config do documento).
     */
    public showImageNumber = computed<boolean>(
        () => this.item()?.configs?.showImageNumber ?? this.config()?.showImageNumber ?? false
    );
    public showImageDate = computed<boolean>(
        () => this.item()?.configs?.showImageDate ?? this.config()?.showImageDate ?? false
    );

    /** Tamanho da imagem única (pronto ou largura livre), mesma regra do PDF. */
    public singleSize = computed(() =>
        resolveSingleImageSize([this.item()?.configs, this.config()])
    );

    public listImages: { url: string; numberImage: number; date: Date }[] = [];
    public documentItemTypeEnum = DocumentItemTypeEnum;

    /**
     * Proporção (largura/altura) de cada imagem, por índice, medida no `load`.
     * A foto no card usa object-fit, então fica menor que o slot (com letterbox);
     * aplicar esse aspect-ratio à marca d'água faz ela cobrir só a foto, não o card.
     */
    public imageAspects = signal<Record<number, number>>({});

    /** Captura a proporção real de cada imagem quando carrega. */
    public onImageLoad(index: number, event: Event): void {
        const img = event.target as HTMLImageElement;
        if (img.naturalWidth > 0 && img.naturalHeight > 0) {
            this.imageAspects.update(map => ({
                ...map,
                [index]: img.naturalWidth / img.naturalHeight,
            }));
        }
    }

    ngOnInit(): void {
        this._checkImageType();
    }

    private _checkImageType(): void {
        switch (this.item()?.type) {
            case DocumentItemTypeEnum.IMAGE:
                this.listImages = [
                    {
                        url: this.item().value,
                        numberImage: this.item().numberImage,
                        date: this.item().date,
                    },
                ];
                break;
            case DocumentItemTypeEnum.GROUP_IMAGE:
                this.listImages = this.item().value.map(item => ({
                    url: item.url,
                    numberImage: item.numberImage,
                    date: item.date,
                }));
                break;
        }
    }
}
